import React, { InputHTMLAttributes, useEffect, useRef, useState } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import { InputSearch } from "./InputSearch";
import { Coordinates } from "../models/Coordinates";

interface InputSearchCityProps extends InputHTMLAttributes<HTMLInputElement> {
    onPlaceSelect?: (suggestion: PlaceSuggestion | null) => void;
    onPressEnter?: (value: string) => void;
}

interface CustomSearchResult {
    type: 'city' | 'subscription';
    name: string;
    nameMain?: string;
    nameSecondary?: string;
    coordinates?: Coordinates;
    filter?: string;
}

export interface PlaceSuggestion {
    type: 'city' | 'subscription';
    name: string;
    coordinates?: Coordinates;
    filter?: string;
}

const SUBSCRIPTION_TYPES = {
  lifetime: {
    name: 'Lifetime',
    filter: 'lifetime'
  },
  gigachad: {
    name: 'Gigachad',
    filter: 'gigachad'
  },
  yearly: {
    name: 'Yearly',
    filter: 'yearly'
  },
  monthly: {
    name: 'Monthly',
    filter: 'monthly'
  },
  whale: {
    name: 'Whale',
    filter: 'whale'
  },
  sponsor: {
    name: 'Sponsor',
    filter: 'sponsor'
  },
  veteran: {
    name: 'Veteran',
    filter: 'veteran'
  },
  founder: {
    name: 'Founder',
    filter: 'founder'
  },
  nostr: {
    name: 'Nostr',
    filter: 'nostr'
  }
} as const;

const InputSearchCity = React.forwardRef<HTMLInputElement, InputSearchCityProps>(({
    onPlaceSelect,
    onPressEnter,
    ...props
}, ref) => {
    const [city, setCity] = useState("");
    const [showResults, setShowResults] = useState(false);
    const [searchResults, setSearchResults] = useState<CustomSearchResult[]>([]);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '',
        libraries: ["places"],
    });

    const getCustomResults = (value: string): CustomSearchResult[] => {
        const searchTerm = value.toLowerCase();
        
        return Object.entries(SUBSCRIPTION_TYPES)
            .filter(([key]) => key.startsWith(searchTerm) && searchTerm.length > 2)
            .map(([_, subscription]) => ({
                type: 'subscription' as const,
                name: subscription.name,
                filter: subscription.filter
            }));
    };

    const getPlacesPredictions = async (input: string) => {
        if (!isLoaded || !input.trim()) return [];
        
        const service = new window.google.maps.places.AutocompleteService();
        try {
            const predictions = await service.getPlacePredictions({
                input,
                types: ["(cities)"],
            });
            return predictions.predictions.map(prediction => ({
                type: 'city' as const,
                name: prediction.description,
                nameMain: prediction.structured_formatting.main_text,
                nameSecondary: prediction.structured_formatting.secondary_text,
                placeId: prediction.place_id,
            }));
        } catch (error) {
            console.error('Error fetching predictions:', error);
            return [];
        }
    };

    // Handle search input changes
    const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setCity(value);
        
        const results = [...getCustomResults(value)];
        if (value.trim()) {
            const predictions = await getPlacesPredictions(value);
            results.push(...predictions);
        }
        setSearchResults(results);
        setShowResults(true);
    };

    // Handle result selection
    const handleResultSelect = async (result: CustomSearchResult) => {
        if (result.type === 'city' && !result.coordinates) {
            // Fetch coordinates for Google Places result
            const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
            try {
                const place = await new Promise<google.maps.places.PlaceResult>((resolve, reject) => {
                    placesService.getDetails({
                        placeId: (result as any).placeId,
                        fields: ['geometry', 'formatted_address']
                    }, (place, status) => {
                        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
                            resolve(place);
                        } else {
                            reject(status);
                        }
                    });
                });

                if (place.geometry?.location) {
                    const suggestion: PlaceSuggestion = {
                        type: 'city',
                        name: result.name,
                        coordinates: {
                            latitude: place.geometry.location.lat(),
                            longitude: place.geometry.location.lng()
                        }
                    };
                    setCity(result.name);
                    onPlaceSelect?.(suggestion);
                }
            } catch (error) {
                console.error('Error fetching place details:', error);
            }
        } else if (result.type === 'subscription') {
            const suggestion: PlaceSuggestion = {
                type: 'subscription',
                name: result.name,
                filter: result.filter
            };
            setCity(result.name);
            onPlaceSelect?.(suggestion);
        }
        setShowResults(false);
    };

    // Handle key press events
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            
            onPressEnter?.(city);
            
            // Hide the results dropdown
            setShowResults(false);
        }
    };

    // Close results when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setShowResults(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (!isLoaded) return null;

    return (
        <div ref={wrapperRef} className="relative">
            <InputSearch
                ref={searchInputRef}
                placeholder="Search for a city..."
                value={city}
                onKeyDown={handleKeyDown}
                onClear={() => {
                    setCity('');
                    setShowResults(false);
                    onPlaceSelect?.(null);
                }}
                onChange={handleSearchChange}
                onFocus={() => setShowResults(true)}
                className={`${showResults && searchResults && searchResults.length > 0 ? 'rounded-b-none' : ''}`}
                {...props}
            />
            
            {
                showResults && searchResults.length > 0 && (
                    <div className="absolute w-full pt-1 rounded-b-lg z-50 max-h-60 overflow-y-auto bg-brand-bg-3">
                        {
                            searchResults.map((result, index) => (
                                <div
                                    key={`${result.type}-${index}`}
                                    onClick={() => handleResultSelect(result)}
                                    className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-brand-bg-4 rounded-lg"
                                >
                                    <span>
                                        <span className="font-semibold">{result.nameMain ?? result.name} </span>
                                        <span className="text-brand-color-2">{result?.nameSecondary}</span>
                                    </span>
                                </div>
                            ))
                        }
                    </div>
                )
            }
        </div>
    );
});

InputSearchCity.displayName = 'InputSearchCity';

export default InputSearchCity;